<template>
    <div
        class="relative w-full"
        ref="wrapperRef"
    >
        <label class="relative">
            <Icon
                icon="credit-card"
                class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10"
            />
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-10 text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                v-model="cardQuery"
                @focus="() => (showCardsList = true)"
                ref="creditCardInput"
                :placeholder="modelValue ? '' : placeholder"
                :disabled="disabled"
                :class="showSelectedOverlay ? 'text-transparent caret-slate-500' : ''"
            />
            <span
                class="z-10 w-[95%] flex items-center gap-2 absolute pr-14 left-10 top-1/2 -translate-y-1/2 whitespace-nowrap truncate pointer-events-none text-slate-500 dark:text-slate-400 text-sm"
                v-if="(props.modelValue || localSelectedCard) && !hasQuery"
            >
                <template v-if="(props.modelValue || localSelectedCard)">
                    <ImageLoader>
                        <template #image>
                            <img
                                :src="selectedCardImageUrl"
                                :alt="selectedCardTitle"
                                class="w-8 h-5 rounded-sm"
                            />
                        </template>
                        <template #error>
                            <Icon
                                icon="credit-card"
                                class="text-slate-600 w-8 h-5"
                            />
                        </template>
                    </ImageLoader>
                    {{ selectedCardTitle }}
                    {{
                        selectedCardForDisplay.last_four
                            ? `(${selectedCardForDisplay.last_four})`
                            : ''
                    }}
                </template>
            </span>
            <button
                v-if="showClear && (hasQuery || modelValue)"
                class="block leading-none text-slate-600 dark:text-slate-400 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                @click="clearCard"
            >
                <Icon icon="close" />
            </button>
        </label>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 min-w-[327px] p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 absolute z-[99999]"
                    v-show="showCardsList && cardQuery"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                    ref="dropdownRef"
                >
                    <div
                        class="mb-2 border-b border-slate-200 dark:border-slate-700 pb-2"
                        v-if="!groupByInstitution && hasResults"
                    >
                        <label
                            v-for="(card, i) in cardResults"
                            :key="'item-id' + i"
                            class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 dark:text-slate-100 text-sm bg-white dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors mb-1 cursor-pointer"
                            @click="selectCard(card)"
                        >
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="
                                            cdnUrl +
                                            '/' +
                                            card?.card_image?.path +
                                            '/_small/' +
                                            card?.card_image?.file_name
                                        "
                                        :alt="card.title"
                                        class="w-8 h-5 rounded-sm"
                                    />
                                </template>
                                <template #error>
                                    <Icon
                                        icon="credit-card"
                                        class="text-slate-600 w-8 h-5"
                                    />
                                </template>
                            </ImageLoader>
                            {{ card.title }}
                            {{ card.last_four ? `(${card.last_four})` : '' }}
                        </label>
                    </div>
                    <template
                        v-for="(cards, issuer) in groupedCards"
                        :key="issuer"
                        v-if="groupByInstitution && hasResults"
                    >
                        <p
                            class="text-slate-500 dark:text-slate-400 text-sm mb-1 pt-1"
                            v-if="showInstitution"
                        >
                            {{ issuer }}
                        </p>
                        <div class="mb-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                            <label
                                v-for="(card, i) in cards"
                                :key="'item-id' + i"
                                class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 dark:text-slate-100 text-sm bg-white dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors mb-1 cursor-pointer"
                                @click="selectCard(card)"
                            >
                                <ImageLoader>
                                    <template #image>
                                        <img
                                            :src="cdnUrl + card.image_url"
                                            :alt="card.title"
                                            class="w-8 h-5 rounded-sm"
                                        />
                                    </template>
                                    <template #error>
                                        <Icon
                                            icon="credit-card"
                                            class="text-slate-600 w-8 h-5"
                                        />
                                    </template>
                                </ImageLoader>
                                {{ card.title }}
                                {{
                                    card.last_four ? `(${card.last_four})` : ''
                                }}
                            </label>
                        </div>
                    </template>
                    <div
                        class="p-3 text-center text-slate-500 dark:text-slate-400 text-sm"
                        v-if="showCardsList && !hasResults && cardQuery"
                    >
                        No cards found matching "{{ cardQuery }}"
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    modelValue: {
        type: Object,
        default: null,
    },
    placeholder: {
        type: String,
        default: 'Search for a card...',
    },
    showClear: {
        type: Boolean,
        default: true,
    },
    institutionCode: {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    showInstitution: {
        type: Boolean,
        default: true,
    },
    groupByInstitution: {
        type: Boolean,
        default: true,
    },
    institutionCode: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:modelValue']);

const cdnUrl = window.cdnUrl;
const cardQuery = ref('');
const showCardsList = ref(false);
const creditCardInput = ref(null);
const width = ref(0);
const top = ref(0);
const left = ref(0);
const groupedCards = ref({});
const cardResults = ref([]);
const localSelectedCard = ref(null);
const wrapperRef = ref(null);
const dropdownRef = ref(null);

const hasQuery = computed(() => {
    return !!(cardQuery.value && cardQuery.value.trim().length > 0);
});

const showSelectedOverlay = computed(() => {
    return !!(selectedCardForDisplay.value && !hasQuery.value);
});

const handleDocumentClick = (event) => {
    const target = event.target;
    const clickedInWrapper = wrapperRef.value?.contains(target);
    const clickedInDropdown = dropdownRef.value?.contains(target);
    if (!clickedInWrapper && !clickedInDropdown) {
        showCardsList.value = false;
    }
};

const loadCards = async () => {
    try {
        if (props.groupByInstitution) {
            const response = await window.axios.get(
                '/api/admin/cards/search-grouped',
                {
                    params: { s: cardQuery.value },
                }
            );
            groupedCards.value = response.data;
        } else {
            const response = await window.ApiService.apiCall(
                `/cards/user-search?s=${cardQuery.value}&institution_code=${props.institutionCode}`,
                'get'
            );
            cardResults.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading cards:', error);
    }
};

const hasResults = computed(() => {
    return (
        cardResults.value?.length > 0 ||
        Object.values(groupedCards.value || {}).length > 0
    );
});

const selectedCardForDisplay = computed(() => {
    return props.modelValue || localSelectedCard.value;
});

const selectedCardImageUrl = computed(() => {
    if (!selectedCardForDisplay.value) return null;
    if (selectedCardForDisplay.value.image_url) {
        return cdnUrl + selectedCardForDisplay.value.image_url;
    }
    if (selectedCardForDisplay.value.card_art_url) {
        return selectedCardForDisplay.value.card_art_url;
    }
    return (
        cdnUrl +
        '/' +
        selectedCardForDisplay.value?.card_image?.path +
        '/_small/' +
        selectedCardForDisplay.value?.card_image?.file_name
    );
});

const selectedCardTitle = computed(() => {
    if (!selectedCardForDisplay.value) return '';
    return (
        selectedCardForDisplay.value.title ||
        selectedCardForDisplay.value.name ||
        selectedCardForDisplay.value.label ||
        selectedCardForDisplay.value.issuer ||
        ''
    );
});

const setCardsDropdownPosition = () => {
    if (!creditCardInput.value) return;
    const rect = creditCardInput.value.getBoundingClientRect();
    width.value = rect.width;
    top.value = rect.bottom + window.scrollY + 4;
    left.value = rect.left + window.scrollX;
};

const selectCard = (card) => {
    localSelectedCard.value = card;
    emit('update:modelValue', card);
    showCardsList.value = false;
    cardQuery.value = '';
    if (creditCardInput.value) {
        creditCardInput.value.blur();
    }
};

const clearCard = () => {
    emit('update:modelValue', null);
    cardQuery.value = '';
    showCardsList.value = false;
};

watch(
    () => props.modelValue,
    (val) => {
        if (val) {
            localSelectedCard.value = val;
            cardQuery.value = '';
            showCardsList.value = false;
        } else {
            localSelectedCard.value = null;
        }
    }
);

watch(cardQuery, () => {
    loadCards();
});

watch(showCardsList, (val) => {
    if (val) {
        setCardsDropdownPosition();
        loadCards();
    }
});

onMounted(() => {
    window.addEventListener('resize', setCardsDropdownPosition);
    window.addEventListener('scroll', setCardsDropdownPosition);
    document.addEventListener('click', handleDocumentClick);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setCardsDropdownPosition);
    window.removeEventListener('scroll', setCardsDropdownPosition);
    document.removeEventListener('click', handleDocumentClick);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
